import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from "react";
import { EdgeLabelRenderer } from "@xyflow/react";
import { ControlPoint } from "./ControlPoint";
import { computeGhostMidpoints } from "./smoothStepPath";
function genWaypointId() {
    // Readable, collision-resistant — one per ghost promotion. Keys are only
    // ever compared within a single edge's waypoint array, so we don't need
    // global uniqueness.
    return `wp_${Math.random().toString(36).slice(2, 10)}`;
}
/**
 * Renders active and ghost control points for a single edge. The edge's
 * renderer decides visibility (typically only in edit mode, while hovered or
 * selected) — we always render them when mounted.
 *
 *  - Active waypoints (solid dots): drag updates position live; right-click
 *    removes them.
 *  - Ghost midpoints (dashed rings): one between each pair of adjacent active
 *    points (source → wp0 → wp1 → … → target). Pointerdown PROMOTES the
 *    ghost into a real waypoint at its current position, then continues the
 *    drag on that new waypoint — so the user gets a single, uninterrupted
 *    gesture from "grab the line" to "drop the bend where I want it."
 */ export function EditableEdgeControls({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, centerX, waypoints, onChange, onCommit, onMouseEnter, onMouseLeave }) {
    // Drafts reflect the live drag state so re-renders stay cheap and the path
    // tracks the pointer without waiting for the backend roundtrip. Committed
    // state flows back in via `waypoints`; the draft is cleared on pointerup.
    const [draft, setDraft] = useState(null);
    const draftRef = useRef(null);
    draftRef.current = draft;
    const effective = draft ?? waypoints;
    const updateAt = useCallback((index, x, y)=>{
        const base = draftRef.current ?? waypoints;
        const next = base.map((w, i)=>i === index ? {
                ...w,
                x,
                y
            } : w);
        draftRef.current = next;
        setDraft(next);
        onChange(next);
    }, [
        waypoints,
        onChange
    ]);
    const commit = useCallback(()=>{
        const final = draftRef.current;
        draftRef.current = null;
        setDraft(null);
        if (final) onCommit(final);
    }, [
        onCommit
    ]);
    const deleteAt = useCallback((index)=>{
        const base = draftRef.current ?? waypoints;
        const next = base.filter((_, i)=>i !== index);
        draftRef.current = null;
        setDraft(null);
        // Delete commits immediately — right-click has no associated drag.
        onCommit(next);
    }, [
        waypoints,
        onCommit
    ]);
    const promoteGhost = useCallback((insertAtIndex, x, y)=>{
        const base = draftRef.current ?? waypoints;
        const newWp = {
            id: genWaypointId(),
            x,
            y
        };
        const next = [
            ...base.slice(0, insertAtIndex),
            newWp,
            ...base.slice(insertAtIndex)
        ];
        draftRef.current = next;
        setDraft(next);
        onChange(next);
        return insertAtIndex;
    }, [
        waypoints,
        onChange
    ]);
    const ghosts = computeGhostMidpoints({
        source: {
            x: sourceX,
            y: sourceY
        },
        target: {
            x: targetX,
            y: targetY
        },
        sourcePosition,
        targetPosition,
        waypoints: effective,
        centerX
    });
    return /*#__PURE__*/ _jsxs(EdgeLabelRenderer, {
        children: [
            effective.map((wp, index)=>/*#__PURE__*/ _jsx(ControlPoint, {
                    id: wp.id,
                    x: wp.x,
                    y: wp.y,
                    active: true,
                    onDrag: (x, y)=>updateAt(index, x, y),
                    onDragEnd: commit,
                    onDelete: ()=>deleteAt(index),
                    onMouseEnter: onMouseEnter,
                    onMouseLeave: onMouseLeave
                }, wp.id)),
            ghosts.map((g)=>{
                // Closure over the NEWLY-PROMOTED index is resolved lazily via a ref
                // set during onDragStart. This keeps the drag math correct even if
                // other waypoints shift around before the move handler fires.
                const promotedIndexRef = {
                    current: -1
                };
                return /*#__PURE__*/ _jsx(ControlPoint, {
                    id: g.id,
                    x: g.x,
                    y: g.y,
                    active: false,
                    onDragStart: ()=>{
                        promotedIndexRef.current = promoteGhost(g.insertAtIndex, g.x, g.y);
                    },
                    onDrag: (x, y)=>{
                        if (promotedIndexRef.current >= 0) {
                            updateAt(promotedIndexRef.current, x, y);
                        }
                    },
                    onDragEnd: commit,
                    onMouseEnter: onMouseEnter,
                    onMouseLeave: onMouseLeave
                }, g.id);
            })
        ]
    });
}

//# sourceMappingURL=EditableEdgeControls.js.map